import { useState, useCallback } from 'react';
import inputSanitizationService from '../services/inputSanitizationService';

/**
 * Custom React hook for input sanitization and validation
 * @param {string} initialValue - Initial input value
 * @param {string} inputType - Type of input (text, email, phone, etc.)
 * @returns {object} - Hook utilities for sanitized input handling
 */
export const useSanitizedInput = (initialValue = '', inputType = 'text') => {
  const [value, setValue] = useState(initialValue);
  const [errors, setErrors] = useState([]);
  const [isValid, setIsValid] = useState(true);

  const handleChange = useCallback((newValue) => {
    // Real-time sanitization for display
    const sanitized = inputSanitizationService.realTimeSanitize(newValue, inputType);
    setValue(sanitized);

    // Full validation for form submission
    const validation = inputSanitizationService.sanitizeInput(sanitized, inputType);
    setErrors(validation.errors);
    setIsValid(validation.isValid);

    return {
      value: sanitized,
      isValid: validation.isValid,
      errors: validation.errors
    };
  }, [inputType]);

  const reset = useCallback(() => {
    setValue(initialValue);
    setErrors([]);
    setIsValid(true);
  }, [initialValue]);

  const validate = useCallback(() => {
    const validation = inputSanitizationService.sanitizeInput(value, inputType);
    setErrors(validation.errors);
    setIsValid(validation.isValid);
    return validation;
  }, [value, inputType]);

  return {
    value,
    errors,
    isValid,
    handleChange,
    reset,
    validate,
    setValue: (newValue) => handleChange(newValue)
  };
};

/**
 * Custom hook for form-level sanitization
 * @param {object} initialFormData - Initial form data
 * @param {object} fieldTypes - Field type mappings
 * @returns {object} - Form sanitization utilities
 */
export const useSanitizedForm = (initialFormData = {}, fieldTypes = {}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(true);

  const updateField = useCallback((fieldName, value) => {
    // Skip sanitization for non-string values (Date objects, booleans, numbers)
    if (value === null || value === undefined || typeof value !== 'string') {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: []
      }));
      return { value, isValid: true, errors: [] };
    }

    const fieldType = fieldTypes[fieldName] || 'text';
    const sanitized = inputSanitizationService.realTimeSanitize(value, fieldType);
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: sanitized
    }));

    // Validate the field
    const validation = inputSanitizationService.sanitizeInput(sanitized, fieldType);
    setFormErrors(prev => ({
      ...prev,
      [fieldName]: validation.errors
    }));

    return {
      value: sanitized,
      isValid: validation.isValid,
      errors: validation.errors
    };
  }, [fieldTypes]);

  const validateForm = useCallback(() => {
    const validation = inputSanitizationService.sanitizeFormData(formData, fieldTypes);
    setFormErrors(validation.errors);
    setIsFormValid(validation.isValid);
    
    if (!validation.isValid) {
      const allErrors = Object.values(validation.errors).flat();
      inputSanitizationService.showValidationError(allErrors);
    }

    return validation;
  }, [formData, fieldTypes]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setFormErrors({});
    setIsFormValid(true);
  }, [initialFormData]);

  const getFieldProps = useCallback((fieldName) => {
    return {
      value: formData[fieldName] || '',
      onChange: (e) => updateField(fieldName, e.target.value),
      error: formErrors[fieldName] && formErrors[fieldName].length > 0,
      helperText: formErrors[fieldName] && formErrors[fieldName].join(', ')
    };
  }, [formData, formErrors, updateField]);

  return {
    formData,
    formErrors,
    isFormValid,
    updateField,
    validateForm,
    resetForm,
    getFieldProps
  };
};